// src/services/staff.service.ts

import apiService from './api';
import { API_CONFIG } from '../config/api.config';
import type {
  StaffResponse,
  StaffResponseData,
  StaffMember,
  CreateStaffPayload,
  UpdateStaffPayload,
  StaffSearchParams,
  CreateStaffResponse,
  UpdateStaffResponse
} from '../types/api.types';

/**
 * Staff Service pour la gestion du personnel AVEC
 */
export class StaffService {
  /**
   * Récupérer tous les membres du personnel avec pagination et filtres
   */
  async getStaff(params?: StaffSearchParams): Promise<StaffResponse> {
    try {
      const searchParams = new URLSearchParams();
      
      if (params?.page) searchParams.append('page', params.page.toString());
      if (params?.limit) searchParams.append('limit', params.limit.toString());
      if (params?.search) searchParams.append('search', params.search);
      if (params?.role) searchParams.append('role', params.role);
      if (params?.department) searchParams.append('department', params.department);
      if (params?.isActive !== undefined) searchParams.append('isActive', params.isActive.toString());
      if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
      if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder);
      
      const queryString = searchParams.toString();
      const endpoint = queryString ? `${API_CONFIG.STAFF.BASE}?${queryString}` : API_CONFIG.STAFF.BASE;
      
      console.log('Fetching staff from endpoint:', endpoint);
      const response = await apiService.get<StaffResponseData>(endpoint);
      console.log('Raw staff API response:', response);
      // The response from apiService.get is of type ApiResponse<{ staff: StaffMember[] }>
      // We need to transform it to match the StaffResponse type
      const staffResponse: StaffResponse = {
        success: response.success,
        message: response.message || '',
        data: {
          staff: response.data?.staff || []
        },
        pagination: (response as any).pagination || { total: 0, page: 1, limit: 10, totalPages: 1 },
        timestamp: new Date().toISOString()
      };
      
      return staffResponse;
    } catch (error: any) {
      console.error('Erreur lors de la récupération du personnel:', error);
      throw error;
    }
  }

  /**
   * Récupérer un membre du personnel par ID
   */
  async getStaffById(id: string): Promise<StaffMember> {
    try {
      const response = await apiService.get<{ staff: StaffMember }>(
        API_CONFIG.STAFF.BY_ID(id)
      );
      
      if (response.success && response.data) {
        return response.data.staff;
      } else {
        throw new Error(response.message || 'Membre du personnel non trouvé');
      }
    } catch (error: any) {
      console.error(`Erreur lors de la récupération du personnel ${id}:`, error);
      throw error;
    }
  }

  /**
   * Créer un nouveau membre du personnel
   */
  async createStaff(payload: CreateStaffPayload): Promise<StaffMember> {
    try {
      const response: CreateStaffResponse = await apiService.post(
        API_CONFIG.STAFF.REGISTER,
        payload
      );

      if (response.success && response.data) {
        // Transform the response to match our StaffMember interface
        const staffMember: StaffMember = {
          ...response.data.user.profile,
          User: response.data.user,
          Supervisor: null,
          Subordinates: []
        };
        
        return staffMember;
      } else {
        throw new Error(response.message || 'Échec de la création du personnel');
      }
    } catch (error: any) {
      console.error('Erreur lors de la création du personnel:', error);
      throw error;
    }
  }

  /**
   * Mettre à jour un membre du personnel
   */
  async updateStaff(id: string, payload: UpdateStaffPayload): Promise<StaffMember> {
    try {
      const response: UpdateStaffResponse = await apiService.put(
        API_CONFIG.STAFF.BY_ID(id),
        payload
      );

      if (response.success && response.data) {
        return response.data.staff;
      } else {
        throw new Error(response.message || 'Échec de la mise à jour du personnel');
      }
    } catch (error: any) {
      console.error(`Erreur lors de la mise à jour du personnel ${id}:`, error);
      throw error;
    }
  }

  /**
   * Activer un membre du personnel
   */
  async activateStaff(id: string): Promise<StaffMember> {
    try {
      const response = await apiService.post<{ staff: StaffMember }>(
        API_CONFIG.STAFF.ACTIVATE(id)
      );

      if (response.success && response.data) {
        return response.data.staff;
      } else {
        throw new Error(response.message || 'Échec de l\'activation du personnel');
      }
    } catch (error: any) {
      console.error(`Erreur lors de l'activation du personnel ${id}:`, error);
      throw error;
    }
  }

  /**
   * Désactiver un membre du personnel
   */
  async deactivateStaff(id: string, reason?: string): Promise<StaffMember> {
    try {
      const response = await apiService.post<{ staff: StaffMember }>(
        API_CONFIG.STAFF.DEACTIVATE(id),
        { reason }
      );

      if (response.success && response.data) {
        return response.data.staff;
      } else {
        throw new Error(response.message || 'Échec de la désactivation du personnel');
      }
    } catch (error: any) {
      console.error(`Erreur lors de la désactivation du personnel ${id}:`, error);
      throw error;
    }
  }

  /**
   * Rechercher le personnel
   */
  async searchStaff(searchTerm: string, filters?: Partial<StaffSearchParams>): Promise<StaffResponse> {
    try {
      const params: StaffSearchParams = {
        search: searchTerm,
        page: 1,
        limit: 20,
        ...filters
      };

      return await this.getStaff(params);
    } catch (error: any) {
      console.error('Erreur lors de la recherche du personnel:', error);
      throw error;
    }
  }

  /**
   * Obtenir les statistiques du personnel
   */
  async getStaffStats(): Promise<any> {
    try {
      const response = await apiService.get<any>(API_CONFIG.STAFF.STATS);
      
      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error(response.message || 'Échec de la récupération des statistiques');
      }
    } catch (error: any) {
      console.error('Erreur lors de la récupération des statistiques du personnel:', error);
      throw error;
    }
  }

  /**
   * Valider les données du personnel
   */
  validateStaffData(data: CreateStaffPayload | UpdateStaffPayload): string[] {
    const errors: string[] = [];

    if ('email' in data) {
      if (!data.email) {
        errors.push('L\'email est requis');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
        errors.push('Format d\'email invalide');
      }
    }

    if ('password' in data) {
      if (!data.password) {
        errors.push('Le mot de passe est requis');
      } else if (data.password.length < 8) {
        errors.push('Le mot de passe doit contenir au moins 8 caractères');
      }
    }

    if ('firstName' in data && data.firstName && data.firstName.trim().length < 2) {
      errors.push('Le prénom doit contenir au moins 2 caractères');
    }

    if ('lastName' in data && data.lastName && data.lastName.trim().length < 2) {
      errors.push('Le nom doit contenir au moins 2 caractères');
    }

    if ('role' in data && data.role && !['accountant', 'credit_agent'].includes(data.role)) {
      errors.push('Rôle invalide');
    }

    if ('phone' in data && data.phone && !/^\+?[\d\s-()]+$/.test(data.phone)) {
      errors.push('Format de téléphone invalide');
    }

    return errors;
  }

  /**
   * Formater le nom complet d'un membre du personnel
   */
  getFullName(staff: StaffMember): string {
    return `${staff.User.firstName} ${staff.User.lastName}`.trim();
  }

  /**
   * Obtenir le libellé du rôle en français
   */
  getRoleLabel(role: string): string {
    const roleLabels: Record<string, string> = {
      administrator: 'Administrateur',
      accountant: 'Comptable',
      credit_agent: 'Agent de crédit'
    };
    return roleLabels[role] || role;
  }

  /**
   * Vérifier si un membre du personnel peut être modifié
   */
  canEditStaff(staff: StaffMember, currentUser: any): boolean {
    // L'administrateur peut tout modifier
    if (currentUser?.Staff?.role === 'administrator') {
      return true;
    }

    // Un utilisateur ne peut pas modifier un administrateur
    if (staff.role === 'administrator') {
      return false;
    }

    // Un utilisateur ne peut pas se modifier lui-même (sauf admin)
    if (staff.userId === currentUser?.id) {
      return false;
    }

    return false;
  }

  /**
   * Vérifier si un membre du personnel peut être supprimé/désactivé
   */
  canDeleteStaff(staff: StaffMember, currentUser: any): boolean {
    // Même logique que pour l'édition
    return this.canEditStaff(staff, currentUser);
  }
}

// Export singleton instance
const staffService = new StaffService();
export default staffService;